import { isNumber, isObjectLike } from '../ir/guards';

/**
 * These guards are a runtime `nodeType` test, so they take whatever is being asked
 * about: a node, an event's target, or a value that may be a node or the caller's own
 * data. Each narrows to the node type within what it was handed.
 */
function nodeType<T>(value: T): number | undefined {
  if (!isObjectLike(value) || !('nodeType' in value)) return undefined;
  const { nodeType: type } = value;
  return isNumber(type) ? type : undefined;
}

const ELEMENT = 1;
const TEXT = 3;
const COMMENT = 8;
const DOCUMENT = 9;

/**
 * The same tests for a value already known to be a node - a template's copy,
 * a marker, a sibling - read straight off it. These run for every slot of
 * every row, where the general guards' checks of what they were handed cost
 * more than the answer.
 */
export function isTextNode(node: Node): node is Text {
  return node.nodeType === TEXT;
}

export function isCommentNode(node: Node): node is Comment {
  return node.nodeType === COMMENT;
}

export function isElement<T>(value: T): value is T & Element {
  return nodeType(value) === ELEMENT;
}

export function isHtmlElement<T>(value: T): value is T & HTMLElement {
  return isElement(value) && 'style' in value && 'dataset' in value;
}

export function isText<T>(value: T): value is T & Text {
  return nodeType(value) === TEXT;
}

export function isComment<T>(value: T): value is T & Comment {
  return nodeType(value) === COMMENT;
}

export function isDocument<T>(value: T): value is T & Document {
  return nodeType(value) === DOCUMENT;
}
